import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Coins, Crown } from "lucide-react";
import api from "../lib/api";
import { fmtNum } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const money = (v, cur = "USD") =>
  v == null ? "—" : `${cur === "GBP" || cur === "GBp" ? "£" : "$"}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DivTable = ({ items, testId, emptyText }) => (
  <div className="widget overflow-hidden" data-testid={testId}>
    {items.length === 0 ? (
      <p className="p-8 text-sm text-muted-foreground">{emptyText}</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              {["Ticker", "Name", "Sector", "Ex-div date", "Rate / yr", "Yield", "Frequency", "Last paid"].map((h, i) => (
                <th key={h} className={`px-3 py-2.5 overline-label whitespace-nowrap ${i >= 4 && i <= 5 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="font-mono">
            {items.map((d) => (
              <tr key={d.ticker} className="border-b border-border/50 even:bg-white/[0.02]" data-testid={`dividend-row-${d.ticker}`}>
                <td className="px-3 py-2.5"><Link to={`/stock/${d.ticker}`} className="text-primary hover:underline">{d.ticker}</Link></td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground font-body truncate max-w-[180px]">{d.name}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground font-body whitespace-nowrap">{d.sector}</td>
                <td className="px-3 py-2.5 text-white whitespace-nowrap">{d.ex_dividend_date || "Not available"}</td>
                <td className="px-3 py-2.5 text-right text-gray-300">{money(d.rate, d.rate_currency)}</td>
                <td className="px-3 py-2.5 text-right text-gain">{d.yield_pct != null ? `${fmtNum(d.yield_pct)}%` : "—"}</td>
                <td className="px-3 py-2.5 text-xs text-gray-300">{d.frequency || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-gray-300 whitespace-nowrap">{d.last_paid_date || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const IncomeProjection = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [pid, setPid] = useState(null);
  const [income, setIncome] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get("/portfolios").then((r) => {
      setPortfolios(r.data.portfolios);
      if (r.data.portfolios.length) setPid(r.data.portfolios[0].id);
    });
  }, []);

  useEffect(() => {
    if (!pid) return;
    setIncome(null);
    setLocked(false);
    api.get(`/dividends/income/${pid}`)
      .then((r) => setIncome(r.data))
      .catch((e) => {
        if (e?.response?.status === 402) setLocked(true);
      });
  }, [pid]);

  if (portfolios.length === 0)
    return (
      <div className="widget p-8" data-testid="income-no-portfolio">
        <p className="text-sm text-muted-foreground">
          Create a portfolio with holdings to project your annual dividend income.{" "}
          <Link to="/portfolio" className="text-primary hover:underline">Go to Portfolio →</Link>
        </p>
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={pid || ""} onValueChange={setPid}>
          <SelectTrigger className="rounded-sm bg-card h-10 w-64" data-testid="income-portfolio-select">
            <SelectValue placeholder="Select portfolio" />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            {portfolios.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {locked ? (
        <div className="widget p-6 border-yellow-500/40 flex items-start gap-4" data-testid="income-locked-panel">
          <Crown className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-bold text-white tracking-tight">Income projection is a Premium feature</div>
            <p className="text-sm text-muted-foreground mt-1 mb-3">
              See your projected annual dividend income and portfolio yield, holding by holding.
            </p>
            <Link to="/upgrade" data-testid="income-locked-upgrade-link"
              className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary/85 text-white text-sm rounded-sm transition-colors duration-150">
              Upgrade to Premium
            </Link>
          </div>
        </div>
      ) : !income ? (
        <Skeleton className="h-40 rounded-sm bg-card" />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="widget p-4" data-testid="income-total-card">
              <div className="overline-label">Projected annual income</div>
              <div className="font-mono text-2xl text-gain mt-2">{money(income.total_annual_income_usd)}</div>
              <div className="text-xs text-muted-foreground mt-1">based on current dividend rates × your shares</div>
            </div>
            <div className="widget p-4" data-testid="income-yield-card">
              <div className="overline-label">Portfolio yield</div>
              <div className="font-mono text-2xl text-white mt-2">{fmtNum(income.portfolio_yield_pct)}%</div>
              <div className="text-xs text-muted-foreground mt-1">annual income ÷ current portfolio value</div>
            </div>
          </div>

          <div className="widget overflow-hidden" data-testid="income-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    {["Ticker", "Shares", "Rate / share", "Yield", "Frequency", "Next ex-div", "Annual income"].map((h, i) => (
                      <th key={h} className={`px-3 py-2.5 overline-label whitespace-nowrap ${i >= 1 && i !== 4 && i !== 5 ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {income.items.map((it) => (
                    <tr key={it.ticker} className="border-b border-border/50 even:bg-white/[0.02]" data-testid={`income-row-${it.ticker}`}>
                      <td className="px-3 py-2.5"><Link to={`/stock/${it.ticker}`} className="text-primary hover:underline">{it.ticker}</Link></td>
                      <td className="px-3 py-2.5 text-right text-gray-300">{fmtNum(it.shares, 0)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-300">{money(it.rate, it.rate_currency)}</td>
                      <td className="px-3 py-2.5 text-right text-gray-300">{it.yield_pct != null ? `${fmtNum(it.yield_pct)}%` : "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-300">{it.frequency || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-300 whitespace-nowrap">{it.ex_dividend_date || "—"}</td>
                      <td className="px-3 py-2.5 text-right">
                        {it.pays_dividend
                          ? <span className="text-gain">{money(it.annual_income_usd)}</span>
                          : <span className="text-muted-foreground text-xs font-body">No dividend</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-4 py-2.5 text-[11px] text-muted-foreground border-t border-border">{income.pence_note}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default function Dividends() {
  const [cal, setCal] = useState(null);

  useEffect(() => {
    api.get("/dividends/calendar").then((r) => setCal(r.data));
  }, []);

  return (
    <div className="space-y-8 fade-up">
      <div>
        <div className="overline-label mb-2">Income planning</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white">Dividend Calendar</h1>
        {cal && <p className="text-xs font-mono text-muted-foreground mt-2">{cal.coverage_note}</p>}
      </div>

      {!cal ? (
        <Skeleton className="h-72 rounded-sm bg-card" />
      ) : (
        <Tabs defaultValue="upcoming">
          <TabsList className="rounded-sm bg-card border border-border h-10" data-testid="dividend-tabs">
            <TabsTrigger value="upcoming" className="rounded-sm text-xs font-mono" data-testid="tab-upcoming">
              <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> UPCOMING ({cal.upcoming.length})
            </TabsTrigger>
            <TabsTrigger value="recent" className="rounded-sm text-xs font-mono" data-testid="tab-recent">RECENT ({cal.recent.length})</TabsTrigger>
            <TabsTrigger value="nodate" className="rounded-sm text-xs font-mono" data-testid="tab-nodate">NO DATE ({cal.no_date.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="upcoming" className="mt-4">
            <DivTable items={cal.upcoming} testId="upcoming-dividends-table" emptyText="No upcoming ex-dividend dates in the cached data." />
          </TabsContent>
          <TabsContent value="recent" className="mt-4">
            <DivTable items={cal.recent} testId="recent-dividends-table" emptyText="No recent ex-dividend dates." />
          </TabsContent>
          <TabsContent value="nodate" className="mt-4">
            <DivTable items={cal.no_date} testId="nodate-dividends-table"
              emptyText="Every dividend payer in the universe currently has a known ex-dividend date." />
            {cal.non_payers.length > 0 && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="non-payers-note">
                Non-payers in the universe: <span className="font-mono">{cal.non_payers.join(", ")}</span>
              </p>
            )}
          </TabsContent>
        </Tabs>
      )}

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Coins className="w-4 h-4 text-primary" />
          <h2 className="font-heading font-bold text-lg text-white tracking-tight">Your income projection</h2>
        </div>
        <IncomeProjection />
      </div>
    </div>
  );
}
